import React, { useEffect, useMemo, useState } from 'react';
import { FaPrint, FaFileCsv, FaFileInvoiceDollar } from 'react-icons/fa';
import Pagination, { PaginationMeta } from './Pagination';
import { TIPOS_FACTURA, EMISOR } from '../../services/facturaService';
import {
    ComprobanteEmitido, leerComprobantes, fechaComprobante,
} from '../../services/comprobantesStore';
import styles from './styles/IvaVentas.module.css';

/** Alicuota general del prototipo (el catalogo de bazar es todo 21%). */
const ALICUOTA = 21;

const money = (n: number) =>
    new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        .format(Number.isFinite(n) ? n : 0);

const isoHoy = () => new Date().toISOString().slice(0, 10);

const isoInicioMes = () => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

/** 'YYYY-MM-DD' -> Date local, al inicio del dia. */
const desdeISO = (iso: string): Date | null => {
    const [y, m, d] = (iso || '').split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
};

/** Una fila del libro: el comprobante con su IVA ya desagregado. */
interface FilaIva {
    comprobante: ComprobanteEmitido;
    /** Neto gravado. En B y C el precio incluye IVA, asi que se extrae. */
    neto: number;
    iva: number;
    /** Percepciones u otros tributos. El prototipo no los liquida todavia. */
    otrosImpuestos: number;
    total: number;
}

/**
 * Desagrega el IVA de un comprobante.
 *
 * En la factura A el IVA ya viene discriminado, asi que se usa tal cual. En B
 * y C el importe es final: para el libro de IVA hay que separar el neto del
 * impuesto contenido, porque el debito fiscal se declara igual aunque en el
 * papel no se muestre.
 */
const desagregar = (c: ComprobanteEmitido): FilaIva => {
    const total = Number(c.totales?.total) || 0;

    if (TIPOS_FACTURA[c.tipo]?.discriminaIva) {
        return {
            comprobante: c,
            neto: Number(c.totales?.neto) || 0,
            iva: Number(c.totales?.iva) || 0,
            otrosImpuestos: 0,
            total,
        };
    }

    const neto = total / (1 + ALICUOTA / 100);
    return {
        comprobante: c,
        neto,
        iva: total - neto,
        otrosImpuestos: 0,
        total,
    };
};

const IvaVentas: React.FC = () => {
    const [comprobantes, setComprobantes] = useState<ComprobanteEmitido[]>([]);
    const [desde, setDesde] = useState(isoInicioMes());
    const [hasta, setHasta] = useState(isoHoy());
    const [tipo, setTipo] = useState<'Todos' | 'A' | 'B' | 'C'>('Todos');

    useEffect(() => { setComprobantes(leerComprobantes()); }, []);

    /** Comprobantes del rango, ordenados por fecha y numero (orden del libro). */
    const filas = useMemo((): FilaIva[] => {
        const ini = desdeISO(desde);
        const fin = desdeISO(hasta);
        // El "hasta" es inclusivo: se compara contra el final de ese dia.
        if (fin) fin.setHours(23, 59, 59, 999);

        return comprobantes
            .filter(c => {
                if (tipo !== 'Todos' && c.tipo !== tipo) return false;
                const f = fechaComprobante(c.fecha);
                if (!f) return false;
                if (ini && f < ini) return false;
                if (fin && f > fin) return false;
                return true;
            })
            .sort((a, b) => {
                const fa = fechaComprobante(a.fecha)?.getTime() ?? 0;
                const fb = fechaComprobante(b.fecha)?.getTime() ?? 0;
                return fa - fb || a.numero - b.numero;
            })
            .map(desagregar);
    }, [comprobantes, desde, hasta, tipo]);

    /*
     * Paginacion del listado. Los totales y el resumen se calculan SIEMPRE
     * sobre el periodo completo (`filas`), no sobre la pagina: un libro de IVA
     * que sumara solo lo visible daria un debito fiscal equivocado.
     */
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(25);

    useEffect(() => { setPage(1); }, [desde, hasta, tipo, perPage]);

    const totalPages = Math.max(1, Math.ceil(filas.length / perPage));
    const pageSafe = Math.min(page, totalPages);
    const filasPagina = useMemo(
        () => filas.slice((pageSafe - 1) * perPage, pageSafe * perPage),
        [filas, pageSafe, perPage],
    );

    const meta: PaginationMeta = {
        page: pageSafe,
        per_page: perPage,
        total: filas.length,
        total_pages: totalPages,
        has_next: pageSafe < totalPages,
        has_prev: pageSafe > 1,
    };

    /** Totales del periodo, para el pie del libro y el resumen por alicuota. */
    const totales = useMemo(() => filas.reduce(
        (acc, f) => ({
            neto: acc.neto + f.neto,
            iva: acc.iva + f.iva,
            otros: acc.otros + f.otrosImpuestos,
            total: acc.total + f.total,
        }),
        { neto: 0, iva: 0, otros: 0, total: 0 },
    ), [filas]);

    /** Desglose por letra: es como lo pide el resumen del libro de ventas. */
    const porTipo = useMemo(() => {
        const acc = new Map<string, { cantidad: number; neto: number; iva: number; total: number }>();
        filas.forEach(f => {
            const key = f.comprobante.tipo;
            const prev = acc.get(key) || { cantidad: 0, neto: 0, iva: 0, total: 0 };
            acc.set(key, {
                cantidad: prev.cantidad + 1,
                neto: prev.neto + f.neto,
                iva: prev.iva + f.iva,
                total: prev.total + f.total,
            });
        });
        return Array.from(acc.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    }, [filas]);

    const exportarCSV = () => {
        const encabezado = [
            'Fecha', 'Tipo', 'Comprobante', 'Cliente', 'Documento',
            'Condicion IVA', 'Neto Gravado', `IVA ${ALICUOTA}%`, 'Otros Impuestos', 'Total',
        ];
        // Separador ';' y coma decimal: es lo que espera Excel en es-AR.
        const filasCsv = filas.map(f => [
            f.comprobante.fecha,
            f.comprobante.tipo,
            f.comprobante.etiqueta,
            f.comprobante.receptor.razonSocial,
            f.comprobante.receptor.documento || '',
            f.comprobante.receptor.condicionIva,
            money(f.neto),
            money(f.iva),
            money(f.otrosImpuestos),
            money(f.total),
        ]);
        const totalRow = [
            'TOTALES', '', '', '', '', '',
            money(totales.neto), money(totales.iva), money(totales.otros), money(totales.total),
        ];

        const csv = [encabezado, ...filasCsv, totalRow]
            .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(';'))
            .join('\r\n');

        // BOM para que Excel abra los acentos correctamente.
        const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `IVA_Ventas_${desde}_a_${hasta}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className={styles.iva}>
            <header className={styles.head}>
                <div>
                    <h2 className={styles.headTitle}>IVA Ventas</h2>
                    <p className={styles.headSub}>
                        Libro de IVA Ventas · {EMISOR.razonSocial} · CUIT {EMISOR.cuit}
                    </p>
                </div>
                <div className={styles.headActions}>
                    <button type="button" className={styles.ghostBtn} onClick={exportarCSV}>
                        <FaFileCsv /> Exportar CSV
                    </button>
                    <button type="button" className={styles.primaryBtn} onClick={() => window.print()}>
                        <FaPrint /> Imprimir
                    </button>
                </div>
            </header>

            <div className={styles.demoNote}>
                Los comprobantes provienen del módulo de Facturación. En esta demo el CAE
                es simulado, por lo que el libro no tiene validez fiscal.
            </div>

            <div className={styles.filters}>
                <label className={styles.filterField}>
                    <span>Desde:</span>
                    <input type="date" value={desde} onChange={e => setDesde(e.target.value)} />
                </label>
                <label className={styles.filterField}>
                    <span>Hasta:</span>
                    <input type="date" value={hasta} onChange={e => setHasta(e.target.value)} />
                </label>
                <label className={styles.filterField}>
                    <span>Tipo:</span>
                    <select value={tipo} onChange={e => setTipo(e.target.value as typeof tipo)}>
                        <option value="Todos">Todos</option>
                        <option value="A">Factura A</option>
                        <option value="B">Factura B</option>
                        <option value="C">Factura C</option>
                    </select>
                </label>
                <span className={styles.count}>
                    {filas.length} comprobante{filas.length === 1 ? '' : 's'}
                </span>
            </div>

            <div className={styles.kpis}>
                <div className={styles.kpi}>
                    <span className={styles.kpiLabel}>Neto gravado</span>
                    <span className={styles.kpiValue}>$ {money(totales.neto)}</span>
                </div>
                <div className={styles.kpi}>
                    <span className={styles.kpiLabel}>IVA {ALICUOTA}%</span>
                    <span className={styles.kpiValue}>$ {money(totales.iva)}</span>
                </div>
                <div className={styles.kpi}>
                    <span className={styles.kpiLabel}>Otros impuestos</span>
                    <span className={styles.kpiValue}>$ {money(totales.otros)}</span>
                </div>
                <div className={styles.kpi}>
                    <span className={styles.kpiLabel}>Total facturado</span>
                    <span className={styles.kpiValue}>$ {money(totales.total)}</span>
                </div>
            </div>

            <div className={styles.tableWrap}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Fecha</th>
                            <th>Tipo</th>
                            <th>Comprobante</th>
                            <th>Cliente</th>
                            <th>Documento</th>
                            <th>Cond. IVA</th>
                            <th className={styles.num}>Neto Gravado</th>
                            <th className={styles.num}>IVA {ALICUOTA}%</th>
                            <th className={styles.num}>Otros Imp.</th>
                            <th className={styles.num}>Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filas.length === 0 && (
                            <tr>
                                <td colSpan={10} className={styles.empty}>
                                    <FaFileInvoiceDollar /> No hay comprobantes emitidos en el período.
                                </td>
                            </tr>
                        )}

                        {filasPagina.map(f => (
                            <tr key={`${f.comprobante.tipo}-${f.comprobante.etiqueta}`}>
                                <td className={styles.nowrap}>{f.comprobante.fecha}</td>
                                <td>
                                    <span className={`${styles.letra} ${styles[`letra${f.comprobante.tipo}`]}`}>
                                        {f.comprobante.tipo}
                                    </span>
                                </td>
                                <td className={styles.mono}>{f.comprobante.etiqueta}</td>
                                <td className={styles.ellipsis} title={f.comprobante.receptor.razonSocial}>
                                    {f.comprobante.receptor.razonSocial}
                                </td>
                                <td className={styles.mono}>{f.comprobante.receptor.documento || '—'}</td>
                                <td className={styles.muted}>{f.comprobante.receptor.condicionIva}</td>
                                <td className={styles.num}>{money(f.neto)}</td>
                                <td className={styles.num}>{money(f.iva)}</td>
                                <td className={styles.num}>{money(f.otrosImpuestos)}</td>
                                <td className={`${styles.num} ${styles.strong}`}>{money(f.total)}</td>
                            </tr>
                        ))}
                    </tbody>
                    {filas.length > 0 && (
                        <tfoot>
                            <tr>
                                {/* Totales de TODO el periodo, no de la pagina visible. */}
                                <td colSpan={6} className={styles.footLabel}>Totales del período</td>
                                <td className={styles.num}>{money(totales.neto)}</td>
                                <td className={styles.num}>{money(totales.iva)}</td>
                                <td className={styles.num}>{money(totales.otros)}</td>
                                <td className={`${styles.num} ${styles.strong}`}>{money(totales.total)}</td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>

            <Pagination
                meta={meta}
                onPageChange={setPage}
                onPerPageChange={setPerPage}
                itemLabel="comprobantes"
            />

            {porTipo.length > 0 && (
                <section className={styles.resumen}>
                    <h3 className={styles.resumenTitle}>Resumen por tipo de comprobante</h3>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Tipo</th>
                                <th className={styles.num}>Cantidad</th>
                                <th className={styles.num}>Neto Gravado</th>
                                <th className={styles.num}>IVA {ALICUOTA}%</th>
                                <th className={styles.num}>Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {porTipo.map(([letra, r]) => (
                                <tr key={letra}>
                                    <td>
                                        <span className={`${styles.letra} ${styles[`letra${letra}`]}`}>{letra}</span>
                                        <span className={styles.tipoNombre}>
                                            {TIPOS_FACTURA[letra as 'A' | 'B' | 'C']?.nombre}
                                        </span>
                                    </td>
                                    <td className={styles.num}>{r.cantidad}</td>
                                    <td className={styles.num}>{money(r.neto)}</td>
                                    <td className={styles.num}>{money(r.iva)}</td>
                                    <td className={`${styles.num} ${styles.strong}`}>{money(r.total)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </section>
            )}
        </div>
    );
};

export default IvaVentas;
